import { LoginResponse } from '@aaa-ncnu-ie/mypolicy-api-login';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';

import { useEmailLinkMutation } from '../../../api/registration/emailLinkConfirmation';
import {
  handleDirectAccessLogin,
  useLoginMutation,
} from '../../../api/registration/login';
import { useEmailConfirmationMutation } from '../../../api/registration/organic/emailConfirmation';
import WelcomeSignIn from '../../../assets/icons/welcome/WelcomeSignIn';
import ControlledTextField from '../../../components/TextField/ControlledTextField';
import Button from '../../../components/styledComponents/Button';
import StyledLink from '../../../components/styledComponents/Link';
import colors from '../../../constants/colors';
import { useDirectAccessAnalytics } from '../../../hooks/useAnalytics';
import useUpdateTitle from '../../../hooks/useUpdateTitle';
import { useSnackbarsContext } from '../../../store/SnackbarProvider';
import { checkForZipCodeCookieAndRedirect } from '../../../utils/helpers';
import ClubRedirectDialog from '../Dialogs/ClubRedirectDialog';
import ForgotPasswordDialog from '../Dialogs/ForgotPasswordDialog';
import SplitView from '../SplitView';
import { LoginFormValues, loginValidationSchema } from './schemas/login';
import CircularProgress from '../../../components/CircularProgress/CircularProgress';

interface EmailLinkQueryParams {
  email: string;
  firstName: string;
  token: string;
}

const Login = () => {
  const redirecting = checkForZipCodeCookieAndRedirect();
  useUpdateTitle('Sign In');
  const [searchParams, setSearchParams] = useSearchParams();
  const [emailLinkParams, setEmailLinkParams] = useState<EmailLinkQueryParams>(
    {} as EmailLinkQueryParams
  );
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] =
    useState(false);
  const [showClubRedirectDialog, setShowClubRedirectDialog] = useState(false);
  const [loginUrl, setLoginUrl] = useState('');
  const [resendCount, setResendCount] = useState(0);

  const loginMutation = useLoginMutation();
  const emailLinkActivateMutation = useEmailLinkMutation();
  const sendEmailVerificationMutation = useEmailConfirmationMutation();

  const { track } = useDirectAccessAnalytics();
  const { closeSnackbar, enqueueSnackbar } = useSnackbarsContext();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
    setValue,
  } = useForm<LoginFormValues>({
    resolver: yupResolver(loginValidationSchema),
    mode: 'onChange',
  });

  const showResendEmailBanner = (email?: string, firstName?: string) =>
    enqueueSnackbar({
      variant: 'error',
      key: 'email-activate-error',
      title: 'We are unable to process your request right now.',
      content: (
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '18px',
            fontFamily: 'Cabin',
          }}
          component="span"
        >
          <Button
            disableRipple
            disableTouchRipple
            variant="text"
            sx={{
              fontSize: 'inherit',
              fontWeight: 'inherit',
              fontFamily: 'inherit',
              px: 0,
              py: 0,
              width: 'fit-content',
              alignSelf: 'flex-start',
            }}
            onClick={() => handleResendClick(email, firstName)}
          >
            Resend email again
          </Button>{' '}
          to verify your email address.
        </Typography>
      ),
    });

  useEffect(() => {
    if (
      searchParams.has('token') &&
      searchParams.has('email') &&
      searchParams.has('firstName')
    ) {
      const token = searchParams.get('token');
      const email = searchParams.get('email');
      const firstName = searchParams.get('firstName');
      if (token !== null && email !== null && firstName !== null) {
        setEmailLinkParams({ token, email, firstName });
      }
      searchParams.delete('token');
      searchParams.delete('email');
      searchParams.delete('firstName');
      setSearchParams(searchParams);
    }
  }, []);

  const handleResendClick = async (email?: string, firstName?: string) => {
    if (resendCount < 5) {
      try {
        await sendEmailVerificationMutation.mutateAsync({
          email: email ?? emailLinkParams.email,
          firstName: firstName ?? emailLinkParams.firstName,
        });
        setResendCount((resendCount) => resendCount + 1);
      } catch (err) {
        enqueueSnackbar({
          variant: 'error',
          key: 'email-verification-resend-error',
          content:
            'Something went wrong resending your email. Please try again.',
        });
        track('Registration Failed', {
          event_type: 'Automated System Process',
          location: 'Feature Card',
          registration_type: 'Organic',
          flow: 'Email',
          reason:
            'Something went wrong resending your email. Please try again.',
          login_method: 'DA',
        });
      }
    } else {
      track('Registration Failed', {
        event_type: 'Automated System Process',
        location: 'Feature Card',
        registration_type: 'Organic',
        flow: 'Email',
        reason: 'Email verification resend count reached',
        login_method: 'DA',
      });
    }
  };

  const handleEmailActivate = async (token: string, email: string) => {
    try {
      await emailLinkActivateMutation.mutateAsync({
        email,
        transactionId: token,
      });
      setValue('email', email);
    } catch (err) {
      track('User Messaged', {
        event_type: 'Messaged',
        location: 'Feature Card',
        message_type: 'Error',
        contents: 'We are unable to process your request right now.',
      });
      showResendEmailBanner();
    }
  };

  useEffect(() => {
    const { token, email, firstName } = emailLinkParams;
    if (token && email && firstName) {
      handleEmailActivate(token, email);
    }
  }, [emailLinkParams]);

  const onSubmit = async (values: LoginFormValues) => {
    reset();
    closeSnackbar();

    try {
      const res = await loginMutation.mutateAsync({
        email: values.email.toLowerCase(),
        password: values.password,
      });

      if (res.sessionToken) {
        track('MyPolicy Accessed', {
          event_type: 'Link Accessed',
          location: 'Feature Card',
          registration_type: 'Organic',
          flow: 'Email',
          link: window.location.href,
          login_method: 'DA',
        });
        handleDirectAccessLogin(res.sessionToken);
      } else {
        if (res.status === 'REQUIRES_ACTIVATION' && res.firstName) {
          showResendEmailBanner(values.email.toLowerCase(), res.firstName);
        } else {
          throw new Error('No session token returned from Login service');
        }
      }
    } catch (err) {
      const response = (err as AxiosError<LoginResponse>)?.response;
      if (response?.status === 409) {
        setShowClubRedirectDialog(true);
        // @ts-expect-error - LoginResponse error type
        setLoginUrl(response?.data?.loginURL);
      } else {
        // using this message for every other error case may not be correct but we need UX/product direction
        enqueueSnackbar({
          variant: 'error',
          title: 'The information you provided does not match our records.',
          key: 'login-error',
          content: (
            <>
              Please try again or{' '}
              <StyledLink
                underline="none"
                sx={{
                  fontSize: { mobile: 14, tablet: 18 },
                  fontWeight: 400,
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  color: colors.csaaSpectrumBlue50,
                }}
                onClick={() => closeSnackbar('login-error')}
                to="/register"
              >
                create an account
              </StyledLink>
              .
            </>
          ),
        });
      }
    }
  };

  return (
    <SplitView data-testid="login-page" Icon={<WelcomeSignIn />}>
      {redirecting ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          direction="column"
          height="100%"
          width={1}
        >
          <CircularProgress size={120} />
        </Stack>
      ) : (
        <Stack
          justifyContent="center"
          direction="column"
          sx={{ px: { mobile: 2, tablet: 14, desktop: 11 } }}
        >
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: { mobile: 33, tablet: 44 },
              lineHeight: { mobile: '48px', desktop: '56px' },
            }}
          >
            Welcome to MyPolicy!
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: { mobile: 23, tablet: 28 },
              lineHeight: { mobile: '32px', desktop: '40px' },
              mt: { mobile: 1, tablet: 1, desktop: 2 },
              color: colors.csaaDark,
            }}
          >
            Sign up or sign in to manage your auto or property policy.
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              fontWeight: 400,
              fontSize: { mobile: 14, tablet: 16 },
              lineHeight: '24px',
              letterSpacing: '0.3px',
              mt: 3,
              color: colors.csaaDark,
            }}
          >
            Even if you have an existing AAA club account, you will need a
            MyPolicy account to sign in and view your insurance policy.
          </Typography>
          <Stack
            direction="column"
            component="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <ControlledTextField
              name="email"
              control={control}
              className="csaa-qm-encrypt"
              label="Email"
              data-testid="login-email-field"
              sx={{ mt: 2 }}
              InputProps={{
                sx: { background: colors.csaaWhite },
                required: true,
              }}
              inputProps={{
                autoComplete: 'email',
                'data-testid': 'login-email-input',
              }}
              preventWhitespace
            />
            <ControlledTextField
              name="password"
              control={control}
              className="csaa-qm-block"
              label="Password"
              data-testid="login-password-field"
              inputProps={{
                autoComplete: 'current-password',
                'data-testid': 'login-password-input',
              }}
              showVisibilityToggle
              sx={{ mt: 2 }}
            />
            <Button
              aria-haspopup="dialog"
              aria-controls={
                showForgotPasswordDialog ? 'forgot-password-dialog' : undefined
              }
              disableRipple
              disableTouchRipple
              variant="text"
              data-testid="login-forgot-password-button"
              sx={{
                fontSize: { mobile: 14, tablet: 16 },
                fontWeight: { mobile: 500 },
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                px: 0,
                py: 0,
                mt: { mobile: 0, tablet: 1.5 },
                width: 'fit-content',
                alignSelf: 'flex-start',
              }}
              onClick={() => setShowForgotPasswordDialog(true)}
            >
              Forgot password?
            </Button>
            <Stack
              direction={{ mobile: 'column', tablet: 'row' }}
              justifyContent={{ mobile: 'space-between', tablet: 'flex-end' }}
              spacing={2}
              sx={{ mt: { mobile: 3 } }}
            >
              <Button
                variant="outlined"
                data-testid="login-secondary-button"
                color="inherit"
                component={Link}
                to="../register"
                onClick={() => closeSnackbar()}
              >
                Sign up
              </Button>
              <Button
                data-testid="login-primary-button"
                type="submit"
                disabled={!isValid}
                loading={
                  loginMutation.isPending || emailLinkActivateMutation.isPending
                }
                onClick={() => closeSnackbar('login-error')}
              >
                Sign in
              </Button>
            </Stack>
          </Stack>
        </Stack>
      )}
      <ForgotPasswordDialog
        open={showForgotPasswordDialog}
        closeDialog={() => {
          setShowForgotPasswordDialog(false);
        }}
      />
      <ClubRedirectDialog
        open={showClubRedirectDialog}
        closeDialog={() => {
          setShowClubRedirectDialog(false);
        }}
        loginUrl={loginUrl}
        loginRedirect
      />
    </SplitView>
  );
};

export default Login;
